import { Button, Input } from "@attmos/flowstack-ui";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { BUDGET_QUERY_KEY, useStartDeposit } from "@/api/budget";
import { ApiError } from "@/api/client";

// The server's bounds (MIN_DEPOSIT_USD / MAX_DEPOSIT_USD); it re-checks them.
const MIN_USD = 1;
const MAX_USD = 10_000;
// Whole cents, as a decimal string: the amount never passes through a float
// on its way to the server.
const AMOUNT = /^\d{1,5}(\.\d{1,2})?$/;
// Stripe's hosted Checkout; the browser is sent nowhere else.
const CHECKOUT_HOST = "checkout.stripe.com";
// Stripe's webhook usually lands within seconds of the return.
const REFRESH_AFTER_MS = [2_000, 6_000, 15_000];

/**
 * Add funds by card through Stripe Checkout (U4.1, fs#1218). Starting a
 * deposit sends the browser to Stripe; Stripe sends it back to this page with
 * `?deposit=success` or `?deposit=cancelled`. The balance moves only when
 * Stripe's webhook credits the wallet, so a return re-reads the budget.
 */
export function AddFunds({ redirect = goTo }: { redirect?: (url: string) => void }) {
  const start = useStartDeposit();
  const [amount, setAmount] = React.useState("");
  const valid = isDepositAmount(amount);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!valid) return;
    start.mutate(amount, {
      onSuccess: (started) => {
        if (isStripeCheckout(started.checkout_url)) redirect(started.checkout_url);
      },
    });
  };
  const badCheckout = start.isSuccess && !isStripeCheckout(start.data.checkout_url);

  return (
    <div className="space-y-2">
      <DepositReturn />
      <form onSubmit={onSubmit} className="flex flex-col gap-1" aria-label="Add funds">
        <div className="flex items-center gap-2">
          <Input
            aria-label="Amount in USD"
            placeholder="50.00"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value.trim())}
            className="w-32"
          />
          <Button type="submit" size="sm" disabled={!valid || start.isPending}>
            Add funds
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          By card through Stripe, in whole cents from $1.00 to $10,000.00.
        </p>
        {amount !== "" && !valid ? (
          <p role="alert" className="text-xs text-destructive">
            Enter an amount from 1.00 to 10000.00, at most two decimals.
          </p>
        ) : null}
        {start.isError ? (
          <p role="alert" className="text-xs text-destructive">
            {depositError(start.error)}
          </p>
        ) : null}
        {badCheckout ? (
          <p role="alert" className="text-xs text-destructive">
            The payment page address was not Stripe&apos;s, so it was not opened.
          </p>
        ) : null}
      </form>
    </div>
  );
}

/** The banner for Stripe's return, and the budget re-reads that follow it. */
function DepositReturn() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const outcome = params.get("deposit");

  React.useEffect(() => {
    if (outcome !== "success") return;
    const refresh = () => queryClient.invalidateQueries({ queryKey: BUDGET_QUERY_KEY });
    void refresh();
    const timers = REFRESH_AFTER_MS.map((ms) => setTimeout(() => void refresh(), ms));
    return () => timers.forEach(clearTimeout);
  }, [outcome, queryClient]);

  if (outcome !== "success" && outcome !== "cancelled") return null;
  const dismiss = () => {
    const next = new URLSearchParams(params);
    next.delete("deposit");
    setParams(next, { replace: true });
  };
  return (
    <div role="status" className="flex items-center gap-2 rounded-md border p-3 text-sm">
      <p className="flex-1">
        {outcome === "success"
          ? "Payment received. Your balance updates once Stripe confirms it, usually within seconds."
          : "Deposit cancelled. Nothing was charged."}
      </p>
      <Button type="button" size="sm" variant="ghost" onClick={dismiss}>
        Dismiss
      </Button>
    </div>
  );
}

export function isDepositAmount(value: string): boolean {
  if (!AMOUNT.test(value)) return false;
  // A range check for display only; the server holds the exact bounds.
  const n = Number(value);
  return n >= MIN_USD && n <= MAX_USD;
}

function isStripeCheckout(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname === CHECKOUT_HOST;
  } catch {
    return false;
  }
}

function depositError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.errorCode === "org_suspended") {
      return "Your organization is suspended, so it cannot add funds.";
    }
    if (error.errorCode === "deposits_disabled") {
      return "Deposits are turned off on this deployment.";
    }
    if (error.errorCode === "stripe_unavailable") {
      return "Stripe could not be reached. Try again in a moment.";
    }
    return error.message;
  }
  return "Could not start the deposit.";
}

function goTo(url: string): void {
  window.location.assign(url);
}
